import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SecondaryButton } from "@financial-edge/shared";
import AuthAlert from "../components/auth/AuthAlert";
import AuthContextPanel from "../components/auth/AuthContextPanel";
import AuthShell from "../components/auth/AuthShell";
import PrimaryButton from "../components/layout/PrimaryButton";
import api from "../api/api";
import {
    getAuthErrorCode,
    getAuthRequestId,
    getOtpErrorMessage,
    isNetworkError,
} from "../utils/auth/authDiagnostics";
import { logAuthFlowWarning } from "../utils/auth/authFlowLogger";
import { buildBlockedSignInStatusState, isBlockedSignInInterception } from "../utils/auth/interceptedAccess";
import {
    buildLaunchSearch,
    formatIntentDestinationReference,
    getIntentDetails,
    getEffectiveLaunchContext,
    getPrimaryAuthChannel,
    parseIntent,
    persistLaunchContext,
} from "../utils/auth/intent";
import {
    clearPendingVerificationContext,
    persistPendingVerificationContext,
    readPendingVerificationContext,
} from "../utils/auth/pendingVerification";
import { DEFAULT_OTP_LENGTH, getOtpLengthForChallenge, normaliseOtpLength } from "../utils/auth/otpLength";
import { signOutBrowserSession } from "../utils/session/browserSession";
import { useAuthPageMeta } from "../utils/ui/useAuthPageMeta";

function maskPhone(phone) {
    const digits = String(phone || "").replace(/\D/g, "");
    if (digits.length < 4) {
        return "your mobile number";
    }

    return `mobile ending in ${digits.slice(-4)}`;
}

function maskEmail(email) {
    const trimmed = String(email || "").trim();
    if (!trimmed.includes("@")) {
        return "your email address";
    }

    const [local, domain] = trimmed.split("@");
    const prefix = local.length <= 2 ? `${local[0] || ""}*` : `${local.slice(0, 2)}***`;
    return `${prefix}@${domain}`;
}

export default function VerifyCode() {
    const location = useLocation();
    const navigate = useNavigate();
    const launchContext = getEffectiveLaunchContext(location.search);
    const persistedVerificationContext = readPendingVerificationContext();
    const verificationState = {
        ...(persistedVerificationContext || {}),
        ...((location.state && typeof location.state === "object") ? location.state : {}),
    };
    const intent = parseIntent(verificationState.intent || launchContext.intent);
    const phone = verificationState.phone || null;
    const email = verificationState.email || null;
    const initialChallengeId = verificationState.challengeId || null;
    const initialRequestId = verificationState.requestId || null;
    const methodSelection = verificationState.methodSelection || null;
    const initialChannel = verificationState.channel || getPrimaryAuthChannel(intent);
    const initialEmailDestinationHint =
        verificationState.emailDestinationHint ||
        methodSelection?.email?.destinationHint ||
        null;
    const source = verificationState.source || launchContext.source;
    const returnTo = verificationState.returnTo || launchContext.returnTo;
    const brokerageId = verificationState.brokerageId || launchContext.brokerageId || null;
    const hasExplicitIntent = verificationState.hasExplicitIntent ?? launchContext.hasExplicitIntent;
    const rememberDevice = verificationState.rememberDevice === true || launchContext.rememberDevice === true;
    const initialFallbackReason = verificationState.fallbackReason || null;
    const initialOtpPreview = verificationState.otpPreview || null;
    const initialTestUserBypass = verificationState.testUserBypass === true;
    const initialOtpLength = normaliseOtpLength(
        verificationState.otpLength,
        initialChannel === "sms" ? 4 : DEFAULT_OTP_LENGTH
    );
    const intentDetails = getIntentDetails(intent, source);
    const statusLaunchSearch = buildLaunchSearch({
        intent,
        source,
        returnTo,
        brokerageId,
    });
    const inputRefs = useRef([]);

    const [otpLength, setOtpLength] = useState(initialOtpLength);
    const [otp, setOtp] = useState(() => Array.from({ length: initialOtpLength }, () => ""));
    const [activeChallengeId, setActiveChallengeId] = useState(initialChallengeId);
    const [activeRequestId, setActiveRequestId] = useState(initialRequestId);
    const [activeChannel, setActiveChannel] = useState(initialChannel);
    const [activeEmailDestinationHint, setActiveEmailDestinationHint] = useState(initialEmailDestinationHint);
    const [activeFallbackReason, setActiveFallbackReason] = useState(initialFallbackReason);
    const [activeOtpPreview, setActiveOtpPreview] = useState(initialOtpPreview);
    const [activeTestUserBypass, setActiveTestUserBypass] = useState(initialTestUserBypass);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [errorCode, setErrorCode] = useState("");
    const [errorRequestId, setErrorRequestId] = useState("");

    const [resendMessage, setResendMessage] = useState("");
    const [resendRemaining, setResendRemaining] = useState(60);

    useAuthPageMeta("Enter Verification Code");

    useEffect(() => {
        persistLaunchContext({
            intent,
            source,
            returnTo,
            brokerageId,
            hasExplicitIntent,
            rememberDevice,
        });
    }, [brokerageId, hasExplicitIntent, intent, rememberDevice, returnTo, source]);

    useEffect(() => {
        persistPendingVerificationContext({
            challengeId: activeChallengeId,
            requestId: activeRequestId,
            channel: activeChannel,
            phone,
            email,
            emailDestinationHint: activeEmailDestinationHint,
            intent,
            source,
            returnTo,
            brokerageId,
            hasExplicitIntent,
            rememberDevice,
            fallbackReason: activeFallbackReason,
            otpLength,
            otpPreview: activeOtpPreview,
            testUserBypass: activeTestUserBypass,
            methodSelection,
        });
    }, [
        activeChallengeId,
        activeRequestId,
        activeChannel,
        activeEmailDestinationHint,
        activeFallbackReason,
        activeOtpPreview,
        activeTestUserBypass,
        brokerageId,
        email,
        hasExplicitIntent,
        intent,
        methodSelection,
        otpLength,
        phone,
        rememberDevice,
        returnTo,
        source,
    ]);

    const otpCode = useMemo(() => otp.join(""), [otp]);
    const isComplete = useMemo(() =>
        otp.length === otpLength && otp.every((digit) => /^[0-9]$/.test(digit)),
    [otp, otpLength]);
    const deliveryTarget = activeChannel === "email"
        ? (activeEmailDestinationHint || maskEmail(email))
        : maskPhone(phone);
    const destinationReference = formatIntentDestinationReference(intentDetails.label);
    const verificationDescription = activeTestUserBypass
        ? `Use the ${otpLength}-digit development test code shown below to verify your identity before opening ${destinationReference}.`
        : `We sent a ${otpLength}-digit code to ${deliveryTarget}. Enter it below to verify your identity before opening ${destinationReference}.`;
    const deliveryTargetLabel = activeTestUserBypass ? "Verification account" : "Code sent to";
    const verificationFooter = activeTestUserBypass
        ? "For this development test account, use the code shown on this screen. Financial Edge will never ask you to share this code by phone or email."
        : "If the code expires or does not arrive, request a new one. Financial Edge will never ask you to share this code by phone or email.";

    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    useEffect(() => {
        const hasSmsContext = activeChannel === "sms" && phone;
        const hasEmailContext = activeChannel === "email" && (email || activeEmailDestinationHint || phone);

        if (!activeChallengeId || (!hasSmsContext && !hasEmailContext)) {
            clearPendingVerificationContext();
            navigate(`/login${buildLaunchSearch({ intent, source, returnTo, brokerageId })}`, {
                replace: true,
            });
        }
    }, [activeChallengeId, activeChannel, activeEmailDestinationHint, brokerageId, email, intent, navigate, phone, returnTo, source]);

    useEffect(() => {
        if (resendRemaining <= 0) {
            return undefined;
        }

        const timer = window.setInterval(() => {
            setResendRemaining((seconds) => Math.max(0, seconds - 1));
        }, 1000);

        return () => window.clearInterval(timer);
    }, [resendRemaining]);

    const focusFirstInput = () => {
        inputRefs.current[0]?.focus();
    };

    const submitOtpCode = async (code) => {
        const expectedOtpPattern = new RegExp(`^\\d{${otpLength}}$`);

        if (!expectedOtpPattern.test(code)) {
            setError(`Enter the full ${otpLength}-digit verification code before continuing.`);
            setErrorCode("");
            setErrorRequestId("");
            focusFirstInput();
            return;
        }

        setLoading(true);
        setError("");
        setErrorCode("");
        setErrorRequestId("");

        setResendMessage("");

        try {
            if (!activeChallengeId) {
                throw new Error("The verification challenge could not be found. Request a new code and try again.");
            }

            const verifyResult = await api.verifyOtp({
                challengeId: activeChallengeId,
                code,
                intent,
                channel: activeChannel,
            });
            const resolvedBrokerageId =
                verificationState.brokerageId ||
                launchContext.brokerageId ||
                verifyResult?.access?.applicationForm?.brokerageLink?.brokerageId ||
                verifyResult?.access?.sharedAccessContext?.brokerage?.brokerageId ||
                null;

            if (isBlockedSignInInterception(verifyResult?.access)) {
                clearPendingVerificationContext();
                await signOutBrowserSession();
                navigate(`/auth-status${statusLaunchSearch || ""}`, {
                    replace: true,
                    state: buildBlockedSignInStatusState(verifyResult.access, {
                        intent,
                    }),
                });
                return;
            }

            clearPendingVerificationContext();
            navigate(`/portal-selection${buildLaunchSearch({ intent, source, returnTo, brokerageId: resolvedBrokerageId })}`, {
                replace: true,
                state: {
                    intent,
                    returnTo,
                    resolution: verifyResult?.resolution || null,
                    preverifiedSession: {
                        identity: verifyResult?.identity || null,
                        session: verifyResult?.session || null,
                        access: verifyResult?.access || null,
                    },
                    verificationContext: {
                        phone,
                        email,
                        brokerageId: resolvedBrokerageId,
                    },
                    hasExplicitIntent,
                    rememberDevice,
                },
            });
        } catch (err) {
            logAuthFlowWarning("verification_failed", {
                intent,
                source,
                phone,
                email,
                message: err?.message || "unknown_error",
                code: err?.code || null,
                requestId: err?.requestId || null,
                offline: !navigator.onLine,
            });
            setError(getOtpErrorMessage(err));
            setErrorCode(getAuthErrorCode(err) || "");
            setErrorRequestId(getAuthRequestId(err) || "");
            setOtp(Array.from({ length: otpLength }, () => ""));

            focusFirstInput();
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (value, index) => {
        const digits = value.replace(/\D/g, "");

        if (!digits && value !== "") {
            return;
        }

        const nextOtp = [...otp];

        if (!digits) {
            nextOtp[index] = "";
        } else {
            const digitsToApply = digits.slice(0, otpLength - index);
            digitsToApply.split("").forEach((digit, offset) => {
                nextOtp[index + offset] = digit;
            });
        }

        setOtp(nextOtp);
        setError("");
        setErrorCode("");
        setErrorRequestId("");

        setResendMessage("");

        const isNextComplete = nextOtp.every((digit) => /^[0-9]$/.test(digit));

        if (isNextComplete && !loading) {
            void submitOtpCode(nextOtp.join(""));
            return;
        }

        if (!digits) {
            return;
        }

        const nextFocusIndex = Math.min(index + digits.length, otpLength - 1);
        inputRefs.current[nextFocusIndex]?.focus();
    };

    const handlePaste = (event) => {
        const pastedDigits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, otpLength);

        if (!pastedDigits) {
            return;
        }

        event.preventDefault();
        const nextOtp = Array.from({ length: otpLength }, (_, index) => pastedDigits[index] || "");
        setOtp(nextOtp);
        setError("");
        setErrorCode("");
        setErrorRequestId("");

        setResendMessage("");

        const focusIndex = Math.min(pastedDigits.length, otpLength - 1);
        inputRefs.current[focusIndex]?.focus();

        if (nextOtp.every((digit) => /^[0-9]$/.test(digit)) && !loading) {
            void submitOtpCode(nextOtp.join(""));
        }
    };

    const handleKeyDown = (event, index) => {
        if (event.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
            return;
        }

        if (event.key === "ArrowLeft" && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }

        if (event.key === "ArrowRight" && index < otpLength - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleConfirm = async (event) => {
        event?.preventDefault?.();
        await submitOtpCode(otpCode);
    };

    const handleCancel = () => {
        clearPendingVerificationContext();
        navigate(`/login${buildLaunchSearch({ intent, source, returnTo, brokerageId })}`, {
            replace: true,
        });
    };

    const handleResend = async () => {
        if (resendRemaining > 0 || loading) {
            return;
        }

        setError("");
        setErrorCode("");
        setErrorRequestId("");

        setResendMessage("");

        try {
            const otpResponse = await api.requestOtp({
                phone,
                email,
                intent,
                source,
                brokerageId,
                returnTo,
                channel: activeChannel,
            });

            if (isBlockedSignInInterception(otpResponse?.access)) {
                clearPendingVerificationContext();
                await signOutBrowserSession();
                navigate(`/auth-status${statusLaunchSearch || ""}`, {
                    replace: true,
                    state: buildBlockedSignInStatusState(otpResponse.access, {
                        intent,
                    }),
                });
                return;
            }

            const nextOtpLength = getOtpLengthForChallenge(otpResponse?.challenge, otpLength);
            setOtpLength(nextOtpLength);
            setOtp(Array.from({ length: nextOtpLength }, () => ""));
            setResendRemaining(60);
            const nextChannel = otpResponse?.challenge?.channel || activeChannel;
            const nextEmailDestinationHint = nextChannel === "email"
                ? (otpResponse?.challenge?.delivery?.redactedDestination || otpResponse?.methodSelection?.email?.destinationHint || activeEmailDestinationHint)
                : null;
            const nextFallbackReason = otpResponse?.challenge?.fallbackReason || null;
            const nextOtpPreview = otpResponse?.challenge?.otpPreview || null;
            const nextTestUserBypass = otpResponse?.challenge?.testUserBypass === true;
            const nextDeliveryTarget = nextChannel === "email"
                ? (nextEmailDestinationHint || maskEmail(email))
                : maskPhone(phone);

            setActiveChannel(nextChannel);
            setActiveRequestId(otpResponse?.requestId || null);
            setActiveEmailDestinationHint(nextEmailDestinationHint);
            setActiveFallbackReason(nextFallbackReason);
            setActiveOtpPreview(nextOtpPreview);
            setActiveTestUserBypass(nextTestUserBypass);
            setResendMessage(nextTestUserBypass
                ? "A new development test code is ready. No email or SMS was sent for this test account."
                : `A new code was sent to ${nextDeliveryTarget}.`);
            inputRefs.current[0]?.focus();
            if (otpResponse?.challenge?.challengeId) {
                setActiveChallengeId(otpResponse.challenge.challengeId);
            }
        } catch (err) {
            logAuthFlowWarning("verification_resend_failed", {
                intent,
                source,
                message: err?.message || "unknown_error",
                code: err?.code || null,
                requestId: err?.requestId || null,
                offline: isNetworkError(err),
            });
            setError(getOtpErrorMessage(err));
            setErrorCode(getAuthErrorCode(err) || "");
            setErrorRequestId(getAuthRequestId(err) || "");

        }
    };

    return (
        <AuthShell
            eyebrow="Step 2 of 3"
            title="Enter your verification code"
            description={verificationDescription}
            footer={
                <div className="space-y-3 text-sm text-secondary">
                    <p>{verificationFooter}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        {activeChannel === "sms" ? (
                            <button
                                type="button"
                                onClick={() => {
                                    if (!methodSelection?.email?.available) {
                                        return;
                                    }

                                    navigate("/auth/login-email", {
                                        state: {
                                            allowEmailFallback: true,
                                            phone,
                                            brokerageId,
                                            intent,
                                            source,
                                            returnTo: returnTo || location.pathname,
                                            hasExplicitIntent,
                                            methodSelection,
                                        },
                                    });
                                }}
                                className={`font-semibold rounded ${methodSelection?.email?.available ? "text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2" : "text-subText cursor-not-allowed"}`}
                                disabled={!methodSelection?.email?.available}
                            >
                                Use backup email instead
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => navigate("/auth/recovery", {
                                state: {
                                    phone,
                                    email,
                                    intent,
                                    source,
                                    returnTo,
                                },
                            })}
                            className="font-semibold text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                        >
                            Need account recovery?
                        </button>
                    </div>
                </div>
            }
        >
            <form onSubmit={handleConfirm} noValidate>
                <AuthContextPanel
                    title="Confirm your secure sign-in"
                    items={[
                        {
                            label: "Workspace",
                            value: intentDetails.label,
                        },
                        {
                            label: deliveryTargetLabel,
                            value: deliveryTarget,
                        },
                    ]}
                    helper="Enter the code on this screen to keep the sign-in tied to this device."
                />

                {activeFallbackReason === "sms_delivery_unavailable" ? (
                    <div className="mt-4">
                        <AuthAlert tone="success" title="Backup email used">
                            SMS delivery is temporarily unavailable, so we sent your secure code to the backup email on file instead.
                        </AuthAlert>
                    </div>
                ) : null}

                {activeOtpPreview ? (
                    <div className="mt-4">
                        <AuthAlert tone="info" title="Development test code">
                            {activeTestUserBypass
                                ? "No email or SMS is sent for this test account. "
                                : "Use this non-production verification code. "}
                            Code: <span className="font-mono font-semibold">{activeOtpPreview}</span>
                        </AuthAlert>
                    </div>
                ) : null}

                <fieldset className="mt-6">
                    <legend className="text-sm font-medium text-foreground">{otpLength}-digit verification code</legend>
                    <div className="mt-4 flex flex-wrap gap-3 justify-start sm:justify-between" onPaste={handlePaste}>
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={(element) => {
                                    inputRefs.current[index] = element;
                                }}
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete={index === 0 ? "one-time-code" : "off"}
                                aria-label={`Digit ${index + 1} of ${otpLength}`}
                                maxLength={1}
                                value={digit}
                                onChange={(event) => handleChange(event.target.value, index)}
                                onKeyDown={(event) => handleKeyDown(event, index)}
                                className="w-14 h-14 md:w-16 md:h-16 border border-lightBorder rounded-[12px] text-center text-2xl font-semibold outline-none focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 transition shadow-sm"
                            />
                        ))}
                    </div>
                </fieldset>

                <div className="mt-5 flex flex-col gap-3 text-sm">
                    <button
                        type="button"
                        onClick={handleResend}
                        disabled={resendRemaining > 0 || loading}
                        className={`text-left font-semibold ${resendRemaining > 0 || loading ? "text-subText cursor-not-allowed" : "text-primary hover:underline"}`}
                    >
                        {resendRemaining > 0 ? `Resend available in ${resendRemaining}s` : "Resend code"}
                    </button>

                    {resendMessage ? (
                        <AuthAlert tone="success" title="Code sent">
                            {resendMessage}
                        </AuthAlert>
                    ) : null}
                </div>

                {error ? (
                    <div className="mt-4 space-y-2">
                        <AuthAlert tone="error" title="Verification could not continue" live="assertive">
                            {error}
                        </AuthAlert>
                        {errorCode ? (
                            <div className="text-xs text-secondary">
                                Error code: <span className="break-all font-mono text-foreground">{errorCode}</span>
                            </div>
                        ) : null}
                        {errorRequestId ? (
                            <div className="text-xs text-secondary">
                                Request ID: <span className="break-all font-mono text-foreground">{errorRequestId}</span>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row">
                    <SecondaryButton
                        type="button"
                        onClick={handleCancel}
                        className="flex-1 text-lg"
                        disabled={loading}
                    >
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        type="submit"
                        className="flex-1"
                        disabled={loading || !isComplete}
                        aria-busy={loading}
                    >
                        {loading ? "Verifying identity..." : "Continue"}
                    </PrimaryButton>
                </div>
            </form>
        </AuthShell>
    );
}
